import { BaseSyntheticEvent, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FieldErrors, useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import SimpleBreadcrumbsPage from '../../../../shared/simple-breadcrumbs-page';
import SimpleFormPageNew from '../../../../shared/simple-form-page-new';
import SharedButton from '../../../../shared/buttons/shared-button';
import ScopesAlert from '../../../../shared/scopes-alert';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../../shared/unsaved-changes-dialog';
import { StepConfig } from '../../../../shared/shared-form-stepper-new';
import { pagesConfig } from '../../../../shared/pages-config';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { useStore } from '../../../state/zustand-store';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { handleErrors } from '../../../../utils/errors';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import NotFoundPage from '../../../../shared/not-found-page';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import {
  getAllDriveLabelsForManagerQuery,
  getDriveLabelPermissionsQuery,
} from '../../../../api/users-api';
import {
  ClonedFromLabel,
  DriveLabel,
  DriveLabelPermission,
  createDriveLabelDraft,
  driveLabelsWriteAccessQuery,
  LabelPermissionModel,
  publishDriveLabel,
  saveDriveLabelDraft,
  setDriveLabelPermissions,
} from '../../../../api/drive-labels-api';
import StepLabelInformation from './step-label-information';
import StepAddFields from './step-add-fields';
import LabelPermissionsDialog from './label-permissions-dialog';
import LabelPreviewPanel from './label-preview-panel';
import StepRules from './step-rules';
import {
  fromGoogleLabel,
  getCloneLabelDefaultValues,
  toDraftModel,
} from './label-draft-mapper';
import {
  CreateLabelForm,
  createLabelSchema,
  DEFAULT_LABEL_PERMISSIONS,
  fromGooglePermissions,
  getCreateLabelDefaultValues,
  labelFieldsSchema,
  labelInformationSchema,
} from './label-schema';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

const STEPS: StepConfig[] = [
  { label: 'Label Information', schema: labelInformationSchema },
  { label: 'Add Fields', schema: labelFieldsSchema },
  { label: 'Rules' },
];

const INFORMATION_FIELDS = Object.keys(
  labelInformationSchema.shape
) as (keyof CreateLabelForm)[];

const LABEL_MANAGER_PATH = `/${pagesConfig.driveLabelsManager.link}`;

interface CreatedLabel {
  id: string;
  creator: string;
}

type PageMode = 'create' | 'clone';

/**
 * The label being cloned and its permissions, which the clone starts with. Permissions are missing
 * when they couldn't be loaded (the query already reported it); the clone then gets the defaults.
 */
interface CloneSource {
  label: DriveLabel;
  permissions?: DriveLabelPermission[];
}

const BREADCRUMBS = [
  { label: ModuleList.modules.administration.name },
  {
    label: pagesConfig.driveLabelsManager.name,
    path: pagesConfig.driveLabelsManager.link,
  },
];

function PageSpinner() {
  return (
    <SimpleBreadcrumbsPage isSubPage breadcrumbs={BREADCRUMBS}>
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    </SimpleBreadcrumbsPage>
  );
}

/**
 * Shows the Create Label page only when Drive Labels and label operations are enabled for the
 * domain, matching Label Manager. The route itself only checks role and tier, so this also covers
 * opening /label-manager/create or /label-manager/clone/:id directly.
 */
export default function CreateLabelPage({
  pageMode = 'create',
}: {
  pageMode?: PageMode;
}) {
  const { features } = useUserSettings();
  if (
    !isFeatureEnabled('driveLabelsRead', features) ||
    !isFeatureEnabled('driveLabelsQ4', features)
  ) {
    return <NotFoundPage />;
  }
  return pageMode === 'clone' ? <CloneLabelForm /> : <CreateLabelForm />;
}

/**
 * Finds the label to clone in Label Manager's list (already cached when coming from the grid) and
 * loads its permissions, then opens the Create Label form filled in with both.
 */
function CloneLabelForm() {
  const { id = '' } = useParams();
  const { data: labels, isLoading: labelsLoading } = useQuery(
    getAllDriveLabelsForManagerQuery()
  );
  const source = labels?.labels?.find((entry) => entry.label?.id === id)?.label;
  const { data: permissions, isLoading: permissionsLoading } = useQuery({
    ...getDriveLabelPermissionsQuery(id),
    enabled: !!source,
  });

  if (labelsLoading || (source && permissionsLoading)) {
    return <PageSpinner />;
  }
  if (!source) {
    return <NotFoundPage />;
  }
  return <CreateLabelForm source={{ label: source, permissions }} />;
}

function CreateLabelForm({ source }: { source?: CloneSource }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  // Google records the manager account gPanel acts as (not the signed-in user) as the creator, the
  // same email Label Manager shows.
  const { adminEmail, changeLogRoles, primaryDomain } = useUserSettings();

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm<CreateLabelForm>({
    resolver: zodResolver(createLabelSchema),
    defaultValues: source
      ? getCloneLabelDefaultValues(source.label)
      : getCreateLabelDefaultValues(),
    mode: 'onTouched',
  });
  const fieldArray = useFieldArray({
    control,
    name: 'fields',
    keyName: 'rhfKey',
  });

  const { data: hasWriteAccess, isLoading: checkingWriteAccess } = useQuery(
    driveLabelsWriteAccessQuery()
  );

  const [activeStep, setActiveStep] = useState(0);
  const [stepCompleted, setStepCompleted] = useState<boolean[]>(
    STEPS.map(() => false)
  );
  const [createdLabel, setCreatedLabel] = useState<CreatedLabel | null>(null);
  const [lastSavedDate, setLastSavedDate] = useState(0);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  // Chosen before the label exists in Google; applied right after it's created. A clone starts
  // with the source label's permissions.
  const [pendingPermissions, setPendingPermissions] = useState<
    LabelPermissionModel[]
  >(() =>
    source?.permissions
      ? fromGooglePermissions(source.permissions)
      : DEFAULT_LABEL_PERMISSIONS
  );
  // Until the chosen permissions reach Google, every save or publish tries them again, and the
  // dialog and preview keep working on the chosen ones rather than Google's defaults.
  const [permissionsApplied, setPermissionsApplied] = useState(false);
  // SimpleFormPageNew's Esc listener would discard the page while dnd-kit cancels a drag with Esc.
  const isDraggingRef = useRef(false);

  const labelId = createdLabel?.id ?? '';
  // Recorded in the create audit log, so it shows which label this one was cloned from.
  const clonedFrom: ClonedFromLabel | undefined = source && {
    id: source.label.id ?? '',
    title: source.label.properties?.title ?? '',
  };

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: confirmLeave,
    handleCancel: cancelLeave,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  const requiresChangeLog = isAbleToSeeUIElement(
    changeLogRoles,
    DriveRoles.CreateDriveLabels.value
  );

  /** Applies the permissions chosen before the label existed, unless they already reached Google. */
  const applyPendingPermissions = async (id: string, message: string) => {
    if (permissionsApplied) {
      return;
    }
    const saved = await setDriveLabelPermissions(
      id,
      pendingPermissions,
      message
    );
    queryClient.setQueryData(getDriveLabelPermissionsQuery(id).queryKey, saved);
    setPermissionsApplied(true);
  };

  const applySavedLabel = (label: DriveLabel) => {
    reset(fromGoogleLabel(label, getValues()));
    setLastSavedDate(Date.now());
  };

  // Nothing exists in Google until the first Save Draft or Publish, which creates the label with
  // the picked apps and learn more link (Google only takes those on create).
  const saveMutation = useMutation({
    mutationFn: async () => {
      let label: DriveLabel;
      let id = labelId;
      if (!id) {
        label = await createDriveLabelDraft(
          toDraftModel(getValues()),
          '',
          clonedFrom
        );
        id = label.id ?? '';
        setCreatedLabel({ id, creator: adminEmail });
      } else {
        label = await saveDriveLabelDraft(id, toDraftModel(getValues()));
      }
      // The draft is saved either way; failed permissions are tried again on the next save.
      let permissionsError: unknown = null;
      try {
        await applyPendingPermissions(id, '');
      } catch (error) {
        permissionsError = error;
      }
      return { label, permissionsError };
    },
    onSuccess: ({ label, permissionsError }) => {
      applySavedLabel(label);
      if (permissionsError) {
        handleErrors({
          error: permissionsError,
          snackbarMessage:
            "The draft was saved, but its permissions couldn't be applied. They'll be tried again when you save or publish.",
        });
        return;
      }
      setSnackbar({ open: true, severity: 'success', message: 'Draft saved.' });
    },
    onError: (error) =>
      handleErrors({
        error,
        snackbarMessage: 'Failed to save the label draft.',
      }),
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      let draft = toDraftModel(getValues());
      let id = labelId;
      if (!id) {
        const created = await createDriveLabelDraft(
          draft,
          changeLogMessage,
          clonedFrom
        );
        id = created.id ?? '';
        setCreatedLabel({ id, creator: adminEmail });
        // Publish saves the draft first, so it needs the ids Google just gave the fields and
        // options; without them that save would delete and recreate every field. They go into the
        // form too, so a retry after a failed publish still sends them.
        applySavedLabel(created);
        draft = toDraftModel(getValues());
      }
      // Before publishing, so the label never goes live with access the admin didn't choose. If
      // this fails, the label stays an unpublished draft and the next publish tries again.
      await applyPendingPermissions(id, changeLogMessage);
      return publishDriveLabel(id, draft, changeLogMessage);
    },
    onSuccess: () => {
      setChangeLogMessage('');
      setChangeLogOpen(false);
      setSnackbar({
        open: true,
        severity: 'success',
        message: source ? 'Label cloned.' : 'Label published.',
      });
      queryClient.invalidateQueries({
        queryKey: getAllDriveLabelsForManagerQuery().queryKey,
      });
      bypass();
      navigate(LABEL_MANAGER_PATH);
    },
    onError: (error) =>
      handleErrors({ error, snackbarMessage: 'Failed to publish the label.' }),
  });

  const isBusy = saveMutation.isPending || publishMutation.isPending;

  const completeStep = (step: number) =>
    setStepCompleted((prev) =>
      prev.map((done, i) => (i === step ? true : done))
    );

  const showInvalid = (formErrors: FieldErrors<CreateLabelForm>) => {
    const infoHasError = INFORMATION_FIELDS.some((key) => formErrors[key]);
    setActiveStep(infoHasError ? 0 : 1);
    setSnackbar({
      open: true,
      severity: 'error',
      message: 'Fix the highlighted fields and try again.',
    });
  };

  const handleNext = async (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const valid =
      activeStep === 0
        ? await trigger(INFORMATION_FIELDS)
        : await trigger('fields');
    if (!valid) {
      return;
    }
    completeStep(activeStep);
    setActiveStep((step) => step + 1);
  };

  const handleSaveDraft = handleSubmit(
    () => saveMutation.mutate(),
    showInvalid
  );

  const handlePublish = handleSubmit(() => {
    if (requiresChangeLog) {
      setChangeLogOpen(true);
    } else {
      publishMutation.mutate();
    }
  }, showInvalid);

  // Leaving never touches Google: an unsaved label doesn't exist there yet, and a saved draft stays.
  const handleCancel = () => {
    if (isDraggingRef.current) {
      return;
    }
    if (isDirty) {
      // The unsaved-changes dialog confirms before leaving.
      navigate(LABEL_MANAGER_PATH, { state: { fromCancelButton: true } });
      return;
    }
    bypass();
    navigate(LABEL_MANAGER_PATH);
  };

  const setDragging = (dragging: boolean) => {
    isDraggingRef.current = dragging;
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <StepLabelInformation
            control={control}
            errors={errors}
            isLabelCreated={!!labelId}
          />
        );
      case 1:
        return (
          <StepAddFields
            control={control}
            errors={errors}
            fieldArray={fieldArray}
            onDragStateChange={setDragging}
          />
        );
      default:
        return <StepRules />;
    }
  };

  if (checkingWriteAccess) {
    return <PageSpinner />;
  }

  if (!hasWriteAccess) {
    return (
      <SimpleBreadcrumbsPage isSubPage breadcrumbs={BREADCRUMBS}>
        <ScopesAlert
          apiKey="driveLabelsWrite"
          additionalText="Labels can't be created until access is granted."
        />
      </SimpleBreadcrumbsPage>
    );
  }

  return (
    <SimpleBreadcrumbsPage isSubPage breadcrumbs={BREADCRUMBS}>
      <SimpleFormPageNew
        title={
          source
            ? pagesConfig.cloneDriveLabel.name
            : pagesConfig.createDriveLabel.name
        }
        trigger={trigger}
        steps={STEPS}
        content={renderStep()}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        stepCompleted={stepCompleted}
        setStepCompleted={setStepCompleted}
        errors={errors}
        handleNext={handleNext}
        handleDiscard={handleCancel}
        handleSubmit={handlePublish}
        submitButtonText="Publish Label"
        isLoadingSubmit={publishMutation.isPending}
        isDisableSubmit={isBusy}
        status="Draft"
        headerActions={
          <Stack direction="row" spacing={1} alignItems="center">
            <SharedButton
              variant="secondary"
              onClick={handleCancel}
              disabled={isBusy}
            >
              Cancel
            </SharedButton>
            <SharedButton
              variant="secondary"
              onClick={handleSaveDraft}
              loading={saveMutation.isPending}
              disabled={isBusy}
            >
              Save Draft
            </SharedButton>
            <SharedButton
              variant="primary"
              onClick={handlePublish}
              loading={publishMutation.isPending}
              disabled={isBusy}
            >
              Publish Label
            </SharedButton>
          </Stack>
        }
        sidePanel={
          <LabelPreviewPanel
            control={control}
            labelId={labelId}
            creator={createdLabel?.creator ?? ''}
            lastModified={lastSavedDate}
            organizationName={primaryDomain}
            pendingPermissions={
              permissionsApplied ? undefined : pendingPermissions
            }
            onEditPermissions={() => setPermissionsOpen(true)}
          />
        }
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Label"
        pageMode={source ? 'clone' : 'create'}
        open={showUnsavedChangesDialog}
        handleConfirm={confirmLeave}
        handleClose={cancelLeave}
      />

      <LabelPermissionsDialog
        open={permissionsOpen}
        handleClose={() => setPermissionsOpen(false)}
        // Edits the chosen permissions until they reach Google, then Google's own.
        labelId={permissionsApplied ? labelId : ''}
        pendingPermissions={pendingPermissions}
        onPendingPermissionsChange={setPendingPermissions}
        organizationName={primaryDomain}
        requiresChangeLog={requiresChangeLog}
      />

      <ChangeLogDialog
        open={changeLogOpen}
        handleClose={() => setChangeLogOpen(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => publishMutation.mutate()}
        mutateLoading={publishMutation.isPending}
      />
    </SimpleBreadcrumbsPage>
  );
}
